// SEQ-180 phase 2: whether one operational metric may be deleted from
// pnl_metric_registry, and what to say when it may not.
//
// Run: deno test --allow-read src/features/pnlAnalytics/operationalMetricDeletion.test.ts
// Run: npm run verify:pnl-operational-metric-deletion
//
// WHY THIS IS A GUARD AND NOT A CONFIRM DIALOG. This check was written when
// pnl_manual_metric_value's metric_key still cascaded, so deleting a metric
// destroyed every number typed under it silently, in the same statement. The
// foreign key now reads `ON DELETE RESTRICT`
// (20260910000400_pnl_manual_metric_value.sql, commit 0aefeb6), so the
// database refuses that delete outright and the data cannot be lost either
// way. The check stays because the database's refusal is a raw foreign key
// violation, which says nothing a user can act on: this one names the count
// and tells them to clear those months first. Construction below,
// explanation here, in that order.
//
// WHY ADMIN ONLY, BEYOND MATCHING THE DATABASE. The registry's write policy
// is org admins only and excludes system rows outright
// (pnl_metric_registry_write_org_admins, FOR ALL, `organization_id IS NOT
// NULL AND ... role IN ('super_admin','admin')`), so a non-admin's delete
// would match zero rows and return no error, which is the silent-revert
// failure this codebase has already been bitten by. There is a second,
// sharper reason: pnl_manual_metric_value's SELECT policy only shows rows
// for projects the caller can see, so a non-admin's count can come back 0
// while values exist on projects they are not a member of. An admin sees
// every project in the organisation through the same policy's admin arm, so
// only an admin's count can be trusted to be complete.
//
// No React and no supabase here: the caller does the counting and the
// writing, this module only decides.

import { isCustomMetricKey } from "../../lib/pnl/customMetricKey";

export type OperationalMetricDeletionRefusal =
  | "not_an_admin"
  | "not_an_organisation_row"
  | "not_a_custom_operational_key"
  | "not_operational"
  | "has_values";

export interface OperationalMetricDeletionInput {
  metricKey: string;
  displayName: string;
  /** The registry row's own organization_id. Null marks a system row (every `calculated.*` and payroll key). */
  rowOrganizationId: string | null;
  /** The registry row's metric_role. */
  metricRole: string;
  /** The registry row's kind. */
  kind: string;
  /** The organisation the viewer is signed in to. */
  viewerOrganizationId: string | null;
  /** Whether the viewer is a super_admin or admin of that organisation. */
  viewerIsOrgAdmin: boolean;
  /** Rows in pnl_manual_metric_value carrying this metric_key for this organisation. */
  valueCount: number;
}

export type OperationalMetricDeletionVerdict =
  | { allowed: true }
  | {
      allowed: false;
      refusal: OperationalMetricDeletionRefusal;
      /** Dialog heading, or toast title. */
      title: string;
      /** One plain sentence or two, safe to show a user as is. */
      message: string;
    };

/** "1 value" / "7 values", so a refusal message never reads "1 values". */
export function describeValueCount(count: number): string {
  return count === 1 ? "1 value" : `${count} values`;
}

/**
 * Decides whether this operational metric may be deleted.
 *
 * The checks run cheapest and broadest first, so the message a user sees
 * names the real reason rather than the last one to fail. `has_values` is
 * last on purpose: being told to clear seven months is only useful advice
 * once the row is one you could otherwise delete.
 */
export function canDeleteOperationalMetric(
  input: OperationalMetricDeletionInput,
): OperationalMetricDeletionVerdict {
  if (!input.viewerIsOrgAdmin) {
    return {
      allowed: false,
      refusal: "not_an_admin",
      title: "Only an admin can delete a metric",
      message:
        "Operational metrics are shared by every project in the organisation, so only an organisation admin can remove one. Ask an admin to delete it for you.",
    };
  }

  if (input.rowOrganizationId === null || input.rowOrganizationId !== input.viewerOrganizationId) {
    return {
      allowed: false,
      refusal: "not_an_organisation_row",
      title: "This metric cannot be deleted",
      message:
        input.rowOrganizationId === null
          ? `${input.displayName} is a built-in metric, not one your organisation created, so it cannot be deleted.`
          : `${input.displayName} belongs to another organisation and cannot be deleted from here.`,
    };
  }

  if (!isCustomMetricKey(input.metricKey) || !input.metricKey.startsWith("operational.custom.")) {
    return {
      allowed: false,
      refusal: "not_a_custom_operational_key",
      title: "This metric cannot be deleted",
      message: `${input.displayName} does not use a custom operational key, so it was not created in this editor and is not safe to delete from it.`,
    };
  }

  if (input.metricRole !== "operational" || input.kind !== "input") {
    return {
      allowed: false,
      refusal: "not_operational",
      title: "This metric cannot be deleted",
      message: `${input.displayName} is not an operational metric, so deleting it here could change a P&L total. Only operational metrics can be removed from this list.`,
    };
  }

  if (input.valueCount > 0) {
    return {
      allowed: false,
      refusal: "has_values",
      title: "Clear the typed values first",
      message: `${input.displayName} has ${describeValueCount(input.valueCount)} typed on project P&L tabs. Deleting the metric would delete those numbers with it. Clear each month on the project's P&L tab first, then delete the metric.`,
    };
  }

  return { allowed: true };
}
